import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { mediaUrl } from '../lib/api';
import { Pin, Sparkles, Crown, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const NEW_THRESHOLD_HOURS = 48;

function formatDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) + ' · ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function isNew(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60);
    return diff < NEW_THRESHOLD_HOURS;
  } catch { return false; }
}

const META = {
  prime: { label: 'Server Prime', color: 'text-[var(--gold)]', bg: 'bg-[var(--gold)]', icon: Crown },
  elite: { label: 'Server Elite', color: 'text-blue-400', bg: 'bg-blue-500', icon: Sparkles },
};

export default function NovitaList() {
  const { server } = useParams();
  const [items, setItems] = useState(null);
  const meta = META[server] || META.prime;
  const token = localStorage.getItem('gtc_token');

  useEffect(() => {
    api.get('/contents', { params: { server } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [server]);

  return (
    <Layout title={`Novità ${meta.label}`} showBack backTo="/novita">
      <div className="px-4 pt-4 max-w-md mx-auto pb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-2.5 h-2.5 rounded-full ${meta.bg}`} />
          <span className="text-xs uppercase tracking-[0.25em] text-white/50">{meta.label}</span>
        </div>

        {items === null && (
          <div className="flex items-center justify-center py-16 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {items?.length === 0 && (
          <div className="text-center py-16 text-white/50 text-sm" data-testid="novita-empty">
            Nessuna pubblicazione al momento.
          </div>
        )}

        <div className="space-y-4">
          {items?.map((c, i) => {
            const fresh = isNew(c.created_at);
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * i }}
              >
                <Link
                  to={`/novita/${server}/${c.id}`}
                  data-testid={`novita-card-${c.id}`}
                  className="card-surface overflow-hidden block relative"
                >
                  {c.image_url && (
                    <div className="relative w-full aspect-video bg-[var(--bg-elevated)] overflow-hidden">
                      <img
                        src={mediaUrl(c.image_url)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {c.is_pinned && (
                        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 text-[10px] text-[var(--gold)]">
                          <Pin className="w-3 h-3" /> In evidenza
                        </div>
                      )}
                      {fresh && (
                        <div className="absolute top-2 right-2 bg-[var(--gold)] text-black text-[10px] font-bold px-2 py-1 rounded-full tracking-widest" data-testid="badge-new">
                          NUOVO
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    {!c.image_url && (
                      <div className="flex items-center gap-2 mb-2">
                        {c.is_pinned && <Pin className="w-3 h-3 text-[var(--gold)]" />}
                        {fresh && <span className="bg-[var(--gold)] text-black text-[10px] font-bold px-2 py-0.5 rounded-full tracking-widest">NUOVO</span>}
                      </div>
                    )}
                    <div className="text-sm text-white/90 line-clamp-3 whitespace-pre-wrap break-words">
                      {c.text}
                    </div>
                    <div className="text-[11px] text-white/40 mt-3">{formatDateTime(c.created_at)}</div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
